import React, { useEffect, useState } from 'react';
import walletApi from '../../lib/walletApi';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { FileText, Download, Loader2, CheckCircle2, Clock, Send, Briefcase } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  draft: { label: 'Draft', icon: FileText, color: 'bg-gray-100 text-gray-600' },
  sent: { label: 'Sent', icon: Send, color: 'bg-blue-50 text-blue-600' },
  viewed: { label: 'Viewed', icon: Clock, color: 'bg-amber-50 text-amber-600' },
  selection_in_progress: { label: 'In Progress', icon: Clock, color: 'bg-violet-50 text-violet-600' },
  accepted: { label: 'Accepted', icon: CheckCircle2, color: 'bg-emerald-50 text-emerald-600' },
  declined: { label: 'Declined', icon: FileText, color: 'bg-red-50 text-red-600' },
  expired: { label: 'Expired', icon: Clock, color: 'bg-gray-100 text-gray-500' },
};

const fmt = (v) => Number(v || 0).toLocaleString('en-NG', { style: 'currency', currency: 'NGN' });

export default function EmployeeOfferLetters() {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await walletApi.get('/offer-letters/my-offers').then(r => r.data);
        setOffers(data.offers || []);
      } catch {
        toast.error('Failed to load offer letters');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleDownload = async (offer) => {
    setDownloading(offer.id);
    try {
      const resp = await walletApi.get(`/offer-letters/my-offers/${offer.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Offer_Letter_${offer.position_title.replace(/\s+/g, '_')}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Offer letter downloaded');
    } catch {
      toast.error('Download failed');
    } finally {
      setDownloading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-7 h-7 animate-spin text-[#D64D7A]" />
      </div>
    );
  }

  return (
    <div className="space-y-5 p-4 max-w-2xl mx-auto" data-testid="employee-offers-page">
      <div>
        <h1 className="text-lg font-bold text-gray-900">My Offer Letters</h1>
        <p className="text-sm text-gray-500">{offers.length} offer letter{offers.length !== 1 ? 's' : ''}</p>
      </div>

      {offers.length === 0 ? (
        <Card className="border-gray-200 border-dashed">
          <CardContent className="py-12 text-center">
            <FileText className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500">No offer letters found for your account</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {offers.map(offer => {
            const cfg = STATUS_CONFIG[offer.status] || STATUS_CONFIG.sent;
            const Icon = cfg.icon;
            return (
              <Card key={offer.id} className="border-gray-200 shadow-sm" data-testid={`offer-card-${offer.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#D64D7A]/10 flex items-center justify-center shrink-0 mt-0.5">
                      <Briefcase className="w-5 h-5 text-[#D64D7A]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-gray-900 text-sm">{offer.position_title}</h3>
                        <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${cfg.color}`}>
                          {cfg.label}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mb-2">{offer.company_name}{offer.department ? ` - ${offer.department}` : ''}</p>
                      <div className="flex items-center gap-4 text-xs text-gray-600">
                        <span>Salary: <span className="font-medium">{fmt(offer.gross_monthly)}/mo</span></span>
                        {offer.benefits_budget > 0 && (
                          <span>Benefits: <span className="font-medium">{fmt(offer.benefits_budget)}/mo</span></span>
                        )}
                      </div>
                      {offer.signed_at && (
                        <p className="text-xs text-emerald-600 mt-1.5 flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3" />
                          Accepted on {new Date(offer.signed_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </p>
                      )}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownload(offer)}
                      disabled={downloading === offer.id}
                      className="shrink-0 h-9 border-gray-200 hover:border-[#D64D7A] hover:text-[#D64D7A]"
                      data-testid={`download-offer-${offer.id}`}
                    >
                      {downloading === offer.id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <><Download className="w-3.5 h-3.5 mr-1" /> PDF</>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
